import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format, formatDistanceToNow } from "date-fns";
import { ArrowLeft, EnvelopeSimple, CheckCircle, Warning, Buildings, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * SpeakerInboxPage — full booking-inquiry inbox for the profile owner.
 *
 * Editorial decision: this page is the UX safety net when the outbound email
 * pipeline (Resend) is misconfigured or throttled. Even if `delivered=false`
 * for every inquiry, the speaker still sees every message here with a
 * one-tap mailto reply button. Email should never be a hard dependency.
 */
export default function SpeakerInboxPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null); // null=loading, false=err/none
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!user || !user.role) return;
    api.get("/me/speaker/inbox")
      .then((r) => setData(r.data))
      .catch(() => setLoadError(true));
  }, [user]);

  if (!user || !user.role) {
    return (
      <div className="px-6 md:px-10 lg:px-16 py-16 max-w-3xl mx-auto text-center">
        <h1 className="font-display font-black text-4xl mb-3">{t("inbox.signInTitle", { defaultValue: "Sign in to see your inbox" })}</h1>
        <p className="text-zinc-500 mb-6">{t("inbox.signInBody", { defaultValue: "Your booking inquiries live behind your Symposia account." })}</p>
        <Link to="/login" className="btn-primary">{t("nav.signIn", { defaultValue: "Sign in" })}</Link>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="px-6 md:px-10 lg:px-16 py-16 max-w-3xl mx-auto text-center" data-testid="inbox-error">
        <Warning size={32} className="text-amber-600 mx-auto mb-3" />
        <h1 className="font-display font-bold text-3xl mb-2">{t("inbox.loadErrorTitle", { defaultValue: "Couldn't load your inbox" })}</h1>
        <p className="text-zinc-500 mb-6">{t("inbox.loadErrorBody", { defaultValue: "Try refreshing the page. If it keeps happening, ping support." })}</p>
        <button onClick={() => window.location.reload()} className="btn-secondary">{t("common.retry", { defaultValue: "Retry" })}</button>
      </div>
    );
  }

  if (!data) {
    return <div className="px-6 md:px-10 lg:px-16 py-16 text-zinc-400 font-mono text-sm">{t("common.loading", { defaultValue: "Loading…" })}</div>;
  }

  const { speaker, inbox, counts } = data;

  if (!speaker) {
    return (
      <div className="px-6 md:px-10 lg:px-16 py-16 max-w-3xl mx-auto text-center" data-testid="inbox-no-profile">
        <Sparkle size={32} className="text-blue-700 mx-auto mb-3" weight="fill" />
        <h1 className="font-display font-bold text-3xl mb-2">{t("inbox.noProfileTitle", { defaultValue: "Publish your profile to start receiving inquiries" })}</h1>
        <p className="text-zinc-500 mb-6">{t("inbox.noProfileBody", { defaultValue: "Once you're live in the speakers directory, organizers can send you booking requests — and they'll all land here." })}</p>
        <Link to="/speakers/me" className="btn-primary">{t("inbox.openEditor", { defaultValue: "Open profile editor" })}</Link>
      </div>
    );
  }

  return (
    <div data-testid="speaker-inbox-page" className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-4xl mx-auto">
      <Link to="/speakers/me" data-testid="inbox-back" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 mb-8">
        <ArrowLeft size={14} /> {t("inbox.backToEditor", { defaultValue: "Back to editor" })}
      </Link>

      <div className="flex items-start justify-between gap-6 flex-wrap border-b border-zinc-950 pb-6 mb-8">
        <div>
          <div className="overline mb-2">{t("inbox.breadcrumb", { defaultValue: "Speaker Inbox" })}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-[-0.03em] leading-none">
            {t("inbox.title", { defaultValue: "Your inbox" })}
          </h1>
          <p className="text-zinc-500 text-sm mt-2 font-mono">{speaker.name}</p>
        </div>
        <div className="flex gap-4">
          <StatPill label={t("inbox.totalLabel", { defaultValue: "TOTAL" })} value={counts.total} testid="inbox-stat-total" />
          <StatPill label={t("inbox.deliveredLabel", { defaultValue: "EMAILED" })} value={counts.delivered} tone="blue" testid="inbox-stat-delivered" />
          {counts.undelivered > 0 && (
            <StatPill label={t("inbox.pendingLabel", { defaultValue: "SAVED ONLY" })} value={counts.undelivered} tone="amber" testid="inbox-stat-undelivered" />
          )}
        </div>
      </div>

      {inbox.length === 0 ? (
        <div className="text-center py-20" data-testid="inbox-empty">
          <EnvelopeSimple size={40} className="text-zinc-300 mx-auto mb-4" />
          <p className="font-display font-semibold text-xl mb-1">{t("inbox.emptyTitle", { defaultValue: "No inquiries yet" })}</p>
          <p className="text-sm text-zinc-500 max-w-md mx-auto">
            {t("inbox.emptyBody", { defaultValue: "Share your profile link — every booking inquiry from the public form will land here, permanently searchable." })}
          </p>
          <Link to={`/speakers/${speaker.id}`} className="btn-secondary mt-6 inline-flex">
            {t("inbox.viewPublic", { defaultValue: "View my public profile" })}
          </Link>
        </div>
      ) : (
        <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 border-y border-zinc-200 dark:border-zinc-800" data-testid="inbox-list">
          {inbox.map((m) => <InboxRow key={m.id} m={m} t={t} />)}
        </ul>
      )}

      {counts.undelivered > 0 && (
        <div className="mt-8 border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4 text-xs font-mono text-amber-900 dark:text-amber-200 flex items-start gap-2" data-testid="inbox-delivery-warning">
          <Warning size={14} className="mt-0.5 flex-shrink-0" />
          <div className="leading-relaxed">
            {counts.undelivered === 1
              ? t("inbox.undeliveredOne", { defaultValue: "1 inquiry is saved but not delivered by email. This usually means the Symposia email service needs domain verification. Reply directly using the buttons above." })
              : t("inbox.undeliveredMany", { n: counts.undelivered, defaultValue: "{{n}} inquiries are saved but not delivered by email. This usually means the Symposia email service needs domain verification. Reply directly using the buttons above." })}
          </div>
        </div>
      )}
    </div>
  );
}

function StatPill({ label, value, tone, testid }) {
  const toneClass = tone === "blue"
    ? "border-blue-700 text-blue-700"
    : tone === "amber"
      ? "border-amber-500 text-amber-700"
      : "border-zinc-300 text-zinc-500";
  return (
    <div className={`border ${toneClass} px-4 py-2 min-w-[80px] text-center`} data-testid={testid}>
      <div className="font-display font-black text-2xl leading-none tabular">{value}</div>
      <div className="text-[9px] font-mono tracking-[0.15em] mt-1.5">{label}</div>
    </div>
  );
}

function InboxRow({ m, t }) {
  const [expanded, setExpanded] = useState(false);
  const created = m.created_at ? new Date(m.created_at) : null;
  const ago = created ? formatDistanceToNow(created, { addSuffix: true }) : "";
  const full = created ? format(created, "MMM d, yyyy · HH:mm") : "";
  const preview = (m.message || "").length > 200 && !expanded ? (m.message || "").slice(0, 200) + "…" : m.message;
  const replyMailto = `mailto:${m.from_email}?subject=${encodeURIComponent(`Re: your Symposia booking inquiry`)}`;

  return (
    <li className="py-5 px-1" data-testid="inbox-row">
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
            <span className="font-display font-semibold text-base text-zinc-950 dark:text-zinc-100" data-testid="inbox-from-name">{m.from_name}</span>
            {m.from_organization && (
              <span className="text-sm text-zinc-500 inline-flex items-center gap-1">
                <Buildings size={11} /> {m.from_organization}
              </span>
            )}
            <span className="text-xs font-mono text-zinc-400 ms-auto" title={full}>{ago}</span>
          </div>
          <a href={`mailto:${m.from_email}`} className="text-xs font-mono text-blue-700 hover:underline block mt-0.5" data-testid="inbox-from-email">
            {m.from_email}
          </a>
          <p className="text-sm text-zinc-700 dark:text-zinc-300 mt-3 leading-relaxed whitespace-pre-line" data-testid="inbox-message">{preview}</p>
          {(m.message || "").length > 200 && !expanded && (
            <button onClick={() => setExpanded(true)} className="text-xs font-mono text-blue-700 hover:underline mt-2">
              {t("inbox.showMore", { defaultValue: "Show full message →" })}
            </button>
          )}
          <div className="flex items-center gap-3 mt-4">
            <a href={replyMailto} data-testid="inbox-reply-btn" className="btn-primary text-xs px-4 py-2 inline-flex items-center gap-1.5">
              <EnvelopeSimple size={12} /> {t("inbox.reply", { defaultValue: "Reply" })}
            </a>
            {m.delivered ? (
              <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-blue-700 flex items-center gap-1">
                <CheckCircle size={11} weight="fill" /> {t("inbox.deliveredBadge", { defaultValue: "Emailed" })}
              </span>
            ) : (
              <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-amber-700 flex items-center gap-1" title="Message is safely saved, but Symposia couldn't deliver it via email (likely a sender-domain config issue). Reply using the button above.">
                <Warning size={11} /> {t("inbox.savedOnlyBadge", { defaultValue: "Saved only" })}
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
